import React, { useState } from 'react';
import { CheckCircle, ChevronRight, Shield, BarChart3, Zap, AlertTriangle } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { T } from '@/utils/translations';

const Onboarding = ({ onComplete }) => {
  const { lang } = useApp();
  const t = T[lang] || T.fr;
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({ risk: 'balanced', assets: ['stocks'] });

  const toggleAsset = (a) => {
    setProfile(p => ({
      ...p,
      assets: p.assets.includes(a) ? p.assets.filter(x => x !== a) : [...p.assets, a]
    }));
  };

  const finish = () => {
    localStorage.setItem('axiom_profile', JSON.stringify(profile));
    localStorage.setItem('axiom_onboarded', 'true');
    onComplete();
  };

  return (
    <div className="onboarding">
      <div className="onboarding-card">
        <div className="onboarding-logo">
          <div className="logo-mark">Ax</div>
          <span className="logo-text">AX<span>-IOM</span></span>
        </div>
        <h2 className="onboarding-title">{t.onboarding.title}</h2>
        <p className="onboarding-subtitle">{t.onboarding.subtitle}</p>

        <div className="steps-indicator">
          <div className={`step-dot ${step >= 1 ? 'active' : ''} ${step > 1 ? 'done' : ''}`}>
            {step > 1 ? <CheckCircle size={14} /> : '1'}
          </div>
          <div className={`step-line ${step > 1 ? 'done' : ''}`} />
          <div className={`step-dot ${step >= 2 ? 'active' : ''} ${step > 2 ? 'done' : ''}`}>
            {step > 2 ? <CheckCircle size={14} /> : '2'}
          </div>
          <div className={`step-line ${step > 2 ? 'done' : ''}`} />
          <div className={`step-dot ${step >= 3 ? 'active' : ''}`}>3</div>
        </div>

        {step === 1 && (
          <>
            <div className="form-label">{t.onboarding.step1}</div>
            <div className="options-list">
              {[
                { id: 'conservative', icon: Shield, color: 'green' },
                { id: 'balanced', icon: BarChart3, color: 'gold' },
                { id: 'aggressive', icon: Zap, color: 'red' }
              ].map(r => (
                <div 
                  key={r.id} 
                  className={`option-card ${profile.risk === r.id ? 'selected' : ''}`} 
                  onClick={() => setProfile({ ...profile, risk: r.id })}
                >
                  <div className={`option-icon ${r.color}`}><r.icon size={20} /></div>
                  <div className="option-info">
                    <div className="option-title">{t.onboarding[r.id]}</div>
                    <div className="option-desc">{t.onboarding[`${r.id}Desc`]}</div>
                  </div>
                  {profile.risk === r.id && <CheckCircle size={20} style={{ color: 'var(--accent-primary)' }} />}
                </div>
              ))}
            </div>
            <button className="btn btn-primary btn-full mt-6" onClick={() => setStep(2)}>
              {t.onboarding.continue} <ChevronRight size={16} />
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div className="form-label">{t.onboarding.step2}</div>
            <div className="asset-grid">
              <div 
                className={`asset-card ${profile.assets.includes('stocks') ? 'selected' : ''}`} 
                onClick={() => toggleAsset('stocks')}
              >
                <div className="asset-icon">📈</div>
                <div className="asset-label">{t.app.stocks}</div>
                {profile.assets.includes('stocks') && <CheckCircle size={18} style={{ color: 'var(--accent-primary)' }} />}
              </div>
              <div 
                className={`asset-card ${profile.assets.includes('crypto') ? 'selected' : ''}`} 
                onClick={() => toggleAsset('crypto')}
              >
                <div className="asset-icon">₿</div>
                <div className="asset-label">{t.app.crypto}</div>
                {profile.assets.includes('crypto') && <CheckCircle size={18} style={{ color: 'var(--accent-primary)' }} />}
              </div>
            </div>
            <button 
              className="btn btn-primary btn-full mt-6" 
              onClick={() => setStep(3)} 
              disabled={profile.assets.length === 0}
            >
              {t.onboarding.continue} <ChevronRight size={16} />
            </button>
          </>
        )}

        {step === 3 && (
          <>
            <div className="form-label">{t.onboarding.step3}</div>
            <div className="confirm-box">
              <div className="confirm-row">
                <span className="confirm-label">{t.onboarding.step1}</span>
                <span className="confirm-value">{t.onboarding[profile.risk]}</span>
              </div>
              <div className="confirm-row">
                <span className="confirm-label">{t.onboarding.step2}</span>
                <span className="confirm-value">{profile.assets.map(a => t.app[a]).join(' & ')}</span>
              </div>
            </div>
            <div className="disclaimer-mini">
              <AlertTriangle size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              {t.disclaimer.text}
            </div>
            <button className="btn btn-primary btn-full" onClick={finish}>
              {t.onboarding.complete} <CheckCircle size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
